import {useState} from "react";
import {Box, Button, CircularProgress, Stack, Typography} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HotelIcon from "@mui/icons-material/Hotel";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "notistack";
import {CancelEvent, ProlongEvent} from "crqlar-react-common/dist/models/floorplan/floorplanEvents.dto";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {convertLocalDateToDate, formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {getIconForLanguage, getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import {useResources} from "crqlar-react-common/dist/utils/hooks/resources/useResources";

interface Props {
    event: CancelEvent | ProlongEvent;
    guestLabel?: string;
    accommodationStayLabel?: string;
    onOpenAccommodationReservation?: (reservation: Reservation, profile?: Profile) => void;
}

const PreviewProfileInformation = ({
                                       event,
                                       guestLabel,
                                       accommodationStayLabel,
                                       onOpenAccommodationReservation,
                                   }: Props) => {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [opening, setOpening] = useState(false);
    // Resolve the room name(s) attached to this guest's accommodation reservation.
    const accommodationResources = useResources(
        event.accommodationReservationsMeta.flatMap(m => m.resourceIds ?? [])
    );

    const profile = event._profile;
    const reservation = event._accommodationReservations[0];
    const meta = event.accommodationReservationsMeta[0];

    const guestCount = reservation?.guestCounts?.reduce((sum, gc) => sum + (gc.count || 0), 0);
    const roomLabel = accommodationResources.find(r => !!r.id && (meta?.resourceIds ?? []).includes(r.id))?.name;

    // Stay range as "07.05.2026 - 10.05.2026" so cross-year stays stay unambiguous.
    // Falls back to the parent-supplied label if dates are missing.
    const stayStart = convertLocalDateToDate(meta?.startDay ?? undefined);
    const stayEnd = convertLocalDateToDate(meta?.endDay ?? undefined);
    const stayRange = stayStart && stayEnd
        ? `${formatFullDMY(stayStart)} - ${formatFullDMY(stayEnd)}`
        : accommodationStayLabel;

    const primaryLanguage = profile?.languageCode || profile?.additionalLanguages?.[0];
    const languageFlag = getIconForLanguage(primaryLanguage);
    const languageName = getNameForLanguage(primaryLanguage, true) || primaryLanguage || "";

    // Refetch the accommodation reservation by id — the embedded snapshot is partial.
    const handleOpen = async () => {
        if (!onOpenAccommodationReservation || !reservation?.id) return;

        setOpening(true);
        try {
            const fresh = await ReservationService.getReservationById(reservation.id);
            if (!fresh) throw new Error("Reservation not found");
            onOpenAccommodationReservation(fresh, profile ?? undefined);
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t("Failed to open reservation"), {
                variant: "error",
            });
        } finally {
            setOpening(false);
        }
    };

    return (
        <Box sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 0.75,
            borderLeft: "3px solid",
            borderLeftColor: "text.secondary",
            px: 2, py: 1.25,
            bgcolor: "background.paper",
        }}>
            <Stack gap={1}>
                {/* Header row — guest name on the left, jump-to-accommodation button on the right. */}
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                    <Box sx={{display: "flex", alignItems: "center", gap: 0.75, minWidth: 0}}>
                        {guestLabel && (
                            <Typography sx={{
                                color: "#344054",
                                fontWeight: 700,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}>
                                {guestLabel}
                            </Typography>
                        )}
                    </Box>
                    {onOpenAccommodationReservation && reservation?.id && (
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={opening}
                            onClick={handleOpen}
                            sx={{position: "relative"}}
                        >
                            <Box component="span" sx={{visibility: opening ? "hidden" : "visible"}}>
                                {t("Hotel reservation")}
                            </Box>
                            {opening && (
                                <CircularProgress
                                    color="inherit"
                                    size={14}
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        marginTop: "-7px",
                                        marginLeft: "-7px",
                                    }}
                                />
                            )}
                        </Button>
                    )}
                </Box>
                <Box sx={{display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 2, rowGap: 0.5}}>
                    {languageFlag && (
                        <Box
                            component="img"
                            alt={languageName}
                            title={languageName}
                            src={languageFlag}
                            sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                objectFit: "cover",
                                border: "0.5px solid rgba(0,0,0,0.15)",
                                flexShrink: 0,
                            }}
                        />
                    )}
                    {guestCount !== undefined && guestCount !== null && (
                        <Box sx={{display: "inline-flex", alignItems: "center", gap: 0.35}}>
                            <Box sx={{display: "inline-flex", color: "text.secondary", "& svg": {fontSize: 16}}}>
                                <PersonOutlineIcon/>
                            </Box>
                            <Typography color="text.secondary" sx={{lineHeight: 1.25, whiteSpace: "nowrap"}}>
                                {guestCount}
                            </Typography>
                        </Box>
                    )}
                    {roomLabel && (
                        <Box sx={{display: "inline-flex", alignItems: "center", gap: 0.35}}>
                            <Box sx={{display: "inline-flex", color: "text.secondary", "& svg": {fontSize: 16}}}>
                                <HotelIcon/>
                            </Box>
                            <Typography color="text.secondary" sx={{lineHeight: 1.25, whiteSpace: "nowrap"}}>
                                {roomLabel}
                            </Typography>
                        </Box>
                    )}
                    {stayRange && (
                        <Box sx={{display: "inline-flex", alignItems: "center", gap: 0.35}}>
                            <Box sx={{display: "inline-flex", color: "text.secondary", "& svg": {fontSize: 16}}}>
                                <CalendarTodayIcon/>
                            </Box>
                            <Typography color="text.secondary" sx={{lineHeight: 1.25, whiteSpace: "nowrap"}}>
                                {stayRange}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

export default PreviewProfileInformation;
